import React, { createRef, ReactElement, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { TransitionGroup } from 'react-transition-group';

import ExpandableContent from './expandableContent/ExpandableContent';
import SavedInstrumentRow from './row/SavedInstrumentRow';
import { getContainerHeight } from '../../../../helpers/commonHelper';
import InputCardDataButton from './InputCardDataButton/InputCardDataButton';
import { Container, ErrorMessage } from './SavedInstrumentsList.styles';
import ChevronUp from '../../../../assets/images/chervronUp.png';
import ChevronDown from '../../../../assets/images/chervronDown.png';
import ChevronButton from './chevronButton/ChevronButton';
import withCardContext from '../../../../hoc/withCardContext';
import { ICardContext } from '../../../../types/CardContext';
import { PROGRESS } from '../../../../config/config';
import arrayMove from './arrayMove/ArrayMove';
import { availablePmpt, PaymentCategoryType } from '../../../../consts';
import { ICardProps } from '../../Card.types';

interface IProps extends ICardProps, IContext, IAccordionWrapper, ICardContext {
  isVisible: boolean;
  handleClickInputCardData: () => void;
  paymentMethods: IMethod[];
}

function SavedInstrumentsList(props: IProps): ReactElement {
  const {
    savedInstruments,
    setTokenizedPaymentAndProgress,
    paymentMethods,
    isMobileOrTablet,
    listExpanded,
    setListExpanded,
    isVisible,
    match: {
      params: { paymentId },
    },
    selectedToken,
    setSelectedToken,
  } = props;

  const [failedToDeleteCard, setFailedToDeleteCard] = useState(false);
  const containerRef = createRef<HTMLDivElement>();

  useEffect(() => {
    const { setHeight, setIsCardComponentLoading, setProgress } = props;
    setIsCardComponentLoading(false);
    if (selectedToken === undefined) {
      setProgress(PROGRESS.SELECT_CARD_METHOD);
    } else {
      setTokenizedPaymentAndProgress(
        { paymentMethodToken: selectedToken },
        true,
        paymentMethods[0].id,
        PaymentCategoryType.CARD,
        availablePmpt.WORLDLINE,
        PROGRESS.CARD_WORLDLINE_PAYMENT_TOKEN_SUCCESS
      );
    }
    if (isVisible && savedInstruments.length > 0) {
      setHeight(getContainerHeight(containerRef.current));
    }
  }, []);

  useEffect(() => {
    const { setProgress } = props;
    if (selectedToken === undefined) setProgress(PROGRESS.SELECT_CARD_METHOD);
  }, [selectedToken]);

  const handleDeleteSavedCard = (failedToDeleteSavedCard: boolean, token: string) => {
    const { setSavedInstruments } = props;
    if (!failedToDeleteSavedCard) {
      setListExpanded(listExpanded);
      setSavedInstruments(savedInstruments.filter(it => it.token !== token));
      if (token === selectedToken) {
        setSelectedToken(undefined);
      }
    }
    setFailedToDeleteCard(failedToDeleteSavedCard);
  };

  const handleClick = (token: string) => {
    setSelectedToken(token);
    setTokenizedPaymentAndProgress(
      { paymentMethodToken: token },
      true,
      paymentMethods[0].id,
      PaymentCategoryType.CARD,
      availablePmpt.WORLDLINE,
      PROGRESS.CARD_WORLDLINE_PAYMENT_TOKEN_SUCCESS
    );
  };

  const handleClickInsertCardData = () => {
    const { setProgressAndFormData, handleClickInputCardData } = props;
    setProgressAndFormData({}, PROGRESS.SELECT_CARD_METHOD);
    handleClickInputCardData();
  };

  const handleExpandButtonClick = () => {
    setListExpanded(true);
  };

  const collapseList = () => {
    if (selectedToken) {
      const { setSavedInstruments } = props;
      const index = savedInstruments?.map(e => e.token).indexOf(selectedToken);
      setSavedInstruments(arrayMove(savedInstruments, index, 0));
    }
    setListExpanded(false);
  };

  const midIndex = savedInstruments.length > 2 ? 1 : 2;
  const secondPart = savedInstruments.slice(midIndex);
  const hasExpandedPart = secondPart.length > 0;

  const getCollapseListButton = () => (
    <ChevronButton
      className="saved-instrument-list__collapse-button"
      textId="savedInstrument.collapse.button"
      handleClick={collapseList}
      icon={ChevronUp}
    />
  );

  const getExpandListButton = () => (
    <ChevronButton
      className="saved-instrument-list__expand-button"
      textId="savedInstrument.expand.button"
      handleClick={handleExpandButtonClick}
      icon={ChevronDown}
    />
  );

  return (
    <Container className="saved-instrument-list__container" ref={containerRef}>
      <ErrorMessage className="saved-instrument-list-delete-card-error-message" isVisible={failedToDeleteCard}>
        <FormattedMessage id="savedInstrument.card.deleteError" />
      </ErrorMessage>
      {savedInstruments.slice(0, midIndex).map((savedInstrument: ISavedInstrument) => (
        <SavedInstrumentRow
          key={`saved-instrument-row-${savedInstrument.token.substring(0, 4)}`}
          handleClick={handleClick}
          isSelected={savedInstrument.token === selectedToken}
          savedInstrument={savedInstrument}
          isMobileOrTablet={isMobileOrTablet}
          paymentId={paymentId}
          handleDeleteSavedCardButtonClick={handleDeleteSavedCard}
        />
      ))}
      <TransitionGroup>
        {hasExpandedPart && (
          <>
            <ExpandableContent expanded={listExpanded}>
              {savedInstruments.slice(midIndex).map((savedInstrument: ISavedInstrument) => (
                <SavedInstrumentRow
                  key={`saved-instrument-row-${savedInstrument.token.substring(0, 4)}`}
                  handleClick={handleClick}
                  isSelected={savedInstrument.token === selectedToken}
                  savedInstrument={savedInstrument}
                  isMobileOrTablet={isMobileOrTablet}
                  paymentId={paymentId}
                  handleDeleteSavedCardButtonClick={handleDeleteSavedCard}
                />
              ))}
            </ExpandableContent>
          </>
        )}
        <InputCardDataButton handleClick={handleClickInsertCardData} />
        {hasExpandedPart && <>{listExpanded ? <>{getCollapseListButton()}</> : <>{getExpandListButton()}</>}</>}
      </TransitionGroup>
    </Container>
  );
}

export default withCardContext(withRouter(SavedInstrumentsList));
